import { TermsAndPrivacyDataType } from './TermsAndPrivacy.types';

export const TermsAndPrivacyData: TermsAndPrivacyDataType = {
  terms: {
    header: {
      title: 'Terms and Conditions',
      description:
        'Your privacy matters to us. We handle all information collected through our compliance app responsibly and in line with industry standards.'
    },
    content: [
      {
        title: 'Terms of Use for the CompliAI Application',
        sections: [
          {
            type: 'paragraph',
            text: 'These Terms of Use (the “Terms”) govern the rights, obligations and responsibilities of users of the CompliAI application and SkyHighGrowth Inc. as the provider and owner of the CompliAI system, in accordance with applicable laws of the United States of America and, where relevant, foreign laws such as Regulation (EU) 2024/1689 – the Artificial Intelligence Act (the “AI Act”), Regulation (EU) 2016/679 (“GDPR”), and Regulation (EU) 2018/1725 on the protection of natural persons with regard to the processing of personal data.  '
          },
          {
            type: 'paragraph',
            text: 'By creating an account, accessing, or using CompliAI via web or any other interface, you confirm that you have read, understood, and fully accept these Terms.  '
          }
        ]
      },
      {
        title: '1. Provider Details and Compliance Statement',
        sections: [
          {
            type: 'keyValue',
            items: [
              {
                label: 'Provider',
                value:
                  'SkyHighGrowth Inc., 276 5th Ave RM 704, New York, NY 10001, USA'
              },
              { label: 'EIN', value: '35-2651156' },
              { label: 'Contact', value: 'compliai@skyhighgrowth.com' }
            ]
          },
          {
            type: 'paragraph',
            text: 'CompliAI is provided as a software-as-a-service (SaaS) platform that assists users with understanding and managing their obligations under the EU AI Act and related regulations, including creating and maintaining technical documentation.'
          },
          {
            type: 'paragraph',
            text: 'Important: CompliAI is a compliance support tool. It does not by itself render your AI system compliant, nor does it replace your own legal, regulatory, or technical assessments.'
          }
        ]
      },
      {
        title: '2. Subject Matter and Description of Services',
        sections: [
          {
            type: 'paragraph',
            text: 'CompliAI is a web application that helps users organise, document, and manage their EU AI Act compliance activities. Depending on the subscription plan and configuration, CompliAI may include, among others, the following features:'
          },
          {
            type: 'bullets',
            items: [
              {
                text: 'AI system inventory and classification (e.g., mapping against AI Act scope, risk categories, Annex III high-risk use cases).'
              },
              {
                text: 'Structured risk assessment workflows and questionnaires.'
              },
              {
                text: 'Generation and management of EU AI Act technical documentation, including:',
                subItems: [
                  'Annex IV-aligned documentation templates,',
                  'risk management records,',
                  'logging and monitoring overviews,',
                  'post-market monitoring plans,',
                  'draft EU Declaration of Conformity templates.'
                ]
              },
              {
                text: 'Workflow support for internal reviews, approvals, and audit trails.'
              },
              {
                text: 'Notifications via e-mail and integrated communication tools (e.g., Slack, Microsoft Teams), where configured.'
              },
              {
                text: 'Reporting dashboards and exportable compliance overviews.'
              }
            ]
          },
          {
            type: 'paragraph',
            text: 'SkyHighGrowth Inc. may update, enhance, or modify CompliAI over time, including adding or removing specific features. Material changes will be communicated through the application and/or via e-mail where appropriate.'
          }
        ]
      },
      {
        title: '3. Privacy and Data Protection',
        sections: [
          {
            type: 'paragraph',
            text: 'SkyHighGrowth Inc. is a company established in the United States and processes personal data in connection with CompliAI in accordance with applicable U.S. federal and state privacy laws and, where relevant, applicable foreign laws (for example, the EU General Data Protection Regulation (“GDPR”) when we offer services to, or monitor behaviour of, individuals in the EU/EEA).'
          }
        ]
      },
      {
        title: '3.1 Categories of Data',
        sections: [
          {
            type: 'paragraph',
            text: 'Depending on how CompliAI is used and configured, SkyHighGrowth Inc. may process:'
          },

          {
            type: 'bullets',
            items: [
              {
                text: 'User and account data: first name, last name, business e-mail address, job title, company name, role, authentication data.'
              },
              {
                text: 'Usage and system data: logs, activity data, device and browser information, IP address, timestamps, and configuration settings.'
              },
              {
                text: 'Customer content: information you upload or input into CompliAI (e.g., AI system descriptions, risk assessments, policies, technical documentation). This content may include personal data if you choose to include such data.'
              }
            ]
          }
        ]
      },
      {
        title: '3.2 Roles and Legal Basis',
        sections: [
          {
            type: 'paragraph',
            text: 'For user and account data, SkyHighGrowth Inc. generally acts as a data controller (or equivalent concept under applicable law). The main legal bases for processing are:'
          },
          {
            type: 'bullets',
            items: [
              {
                text: 'performance of a contract (provision and administration of the service),'
              },
              {
                text: 'legitimate interests (e.g., security, service improvement, fraud prevention), and'
              },
              {
                text: 'consent where required (e.g., certain marketing communications).'
              }
            ]
          },
          {
            type: 'paragraph',
            text: 'For customer content entered into CompliAI, SkyHighGrowth Inc. generally acts as a data processor/service provider on behalf of the organisation that purchased or controls the account (the “Customer”). In those cases:'
          },
          {
            type: 'bullets',
            items: [
              {
                text: 'the Customer is responsible for having a lawful basis for processing personal data, and'
              },
              {
                text: 'our processing is governed by the applicable subscription agreement and/or data processing / data protection addendum between SkyHighGrowth Inc. and the Customer.  '
              }
            ]
          },
          {
            type: 'paragraph',
            text: 'Where GDPR or other non-U.S. privacy laws apply to the processing of personal data, SkyHighGrowth Inc. will comply with those laws to the extent they are applicable (for example, by entering into Standard Contractual Clauses or similar transfer mechanisms, where required).'
          }
        ]
      },
      {
        title: '3.3 Retention',
        sections: [
          {
            type: 'paragraph',
            text: 'We retain personal data for as long as necessary to:'
          },
          {
            type: 'bullets',
            items: [
              {
                text: 'provide CompliAI and related services,'
              },
              {
                text: 'comply with our legal obligations,'
              },
              {
                text: 'resolve disputes, and'
              },
              {
                text: 'enforce our agreements.'
              }
            ]
          },
          {
            type: 'paragraph',
            text: 'Upon a valid deletion request from the Customer or an individual, we will delete or anonymise personal data within a reasonable period, unless we are required or permitted to keep it longer under applicable law (for example, for accounting, tax, fraud-prevention, or security reasons).  '
          },
          {
            type: 'paragraph',
            text: 'Further details are provided in our Privacy Policy, which forms an integral part of your relationship with SkyHighGrowth Inc.  '
          }
        ]
      },
      {
        title: '4. Instructions for Use',
        sections: [
          {
            type: 'paragraph',
            text: 'CompliAI is intended to be used as an internal compliance support tool by organisations and professionals responsible for implementing and monitoring compliance with the EU AI Act and related legislation.  '
          },
          {
            type: 'paragraph',
            text: 'User instructions, documentation, and in-product guidance will typically include:'
          },
          {
            type: 'bullets',
            items: [
              {
                text: 'a description of the application’s main features and limitations;'
              },
              {
                text: 'supported and unsupported use cases;'
              },
              {
                text: 'known constraints regarding accuracy, completeness, robustness, and security of AI-generated outputs (where AI is used);'
              },
              {
                text: 'recommended governance workflows and human oversight measures (e.g., internal review steps, approvals, sign-off processes);'
              },
              {
                text: 'technical requirements (supported browsers, connectivity, hardware resources);'
              },
              {
                text: 'maintenance and update information.'
              }
            ]
          },
          {
            type: 'paragraph',
            text: 'You are obliged to:'
          },
          {
            type: 'bullets',
            items: [
              {
                text: 'use CompliAI only for its intended purpose and in accordance with the documentation;'
              },
              {
                text: 'ensure that only authorised users access the system;'
              },
              {
                text: 'keep login credentials confidential;'
              },
              {
                text: 'implement appropriate internal review and oversight measures before relying on any documentation or outputs generated through CompliAI.  '
              }
            ]
          },
          {
            type: 'paragraph',
            text: 'Nothing in CompliAI removes or replaces your obligation to carry out your own compliance assessments and to engage with regulators or notified bodies where required.  '
          }
        ]
      },
      {
        title: '5. Intellectual Property',
        sections: [
          {
            type: 'paragraph',
            text: 'The CompliAI application and all related content (including but not limited to text, design, source code, user interface components, documentation, logos, databases, and underlying models) are the intellectual property of SkyHighGrowth Inc. or its licensors.'
          },

          {
            type: 'paragraph',
            text: 'Unless expressly permitted in writing by SkyHighGrowth Inc., you may not:'
          },

          {
            type: 'bullets',
            items: [
              {
                text: 'copy, reproduce, distribute, sell, sublicense, publicly display, or otherwise use CompliAI or its content beyond what is necessary for internal use;'
              },
              {
                text: 'reverse engineer, decompile, disassemble, or attempt to derive source code, except to the extent permitted by mandatory law;'
              },
              {
                text: 'remove or alter proprietary notices, trademarks, or copyright notices.'
              }
            ]
          }
        ]
      },
      {
        title: '6. User Responsibilities and Prohibited Conduct',
        sections: [
          {
            type: 'paragraph',
            text: 'By accepting these Terms, you agree that you will not use CompliAI to:'
          },

          {
            type: 'bullets',
            items: [
              {
                text: 'upload, store, or transmit unlawful, offensive, hateful, defamatory, fraudulent, misleading, obscene, or otherwise inappropriate content;'
              },
              {
                text: 'harass, threaten, or abuse other users or third parties;'
              },
              {
                text: 'deploy CompliAI for purposes that are contrary to applicable law, including but not limited to circumventing regulatory requirements or masking unlawful uses of AI;'
              },
              {
                text: 'introduce malware, attempt unauthorised access, probe, or test the vulnerability of any system or network;'
              },
              {
                text: 'violate intellectual property rights, confidentiality obligations, or data protection rules.'
              }
            ]
          },

          {
            type: 'paragraph',
            text: 'SkyHighGrowth Inc. reserves the right to suspend or terminate access to CompliAI for any user or customer who violates these Terms or applicable law and, where required, notify competent authorities.'
          },

          {
            type: 'paragraph',
            text: 'You are solely responsible for the accuracy, lawfulness, and completeness of all data and content you enter into CompliAI, including personal data and technical information about your AI systems.'
          }
        ]
      },
      {
        title: '7. Disclaimer',
        sections: [
          {
            type: 'paragraph',
            text: 'CompliAI and all information, templates, classifications, risk assessments, reports, notifications, and other outputs generated by or through CompliAI are provided for informational and organisational purposes only.'
          },

          {
            type: 'paragraph',
            text: 'They:'
          },

          {
            type: 'bullets',
            items: [
              {
                text: 'do not constitute legal, regulatory, financial, or professional advice;'
              },
              {
                text: 'do not guarantee compliance with the AI Act, GDPR, or any other law;'
              },
              {
                text: 'do not replace your own internal compliance processes, legal counsel, or engagement with supervisory authorities or notified bodies.'
              }
            ]
          },

          {
            type: 'paragraph',
            text: 'To the maximum extent permitted by law:'
          },

          {
            type: 'bullets',
            items: [
              {
                text: 'SkyHighGrowth Inc. is not liable for any decisions, actions, or omissions taken on the basis of outputs or documentation generated using CompliAI;'
              },
              {
                text: 'SkyHighGrowth Inc. is not responsible for any incorrect or incomplete information or documentation resulting from inaccurate, outdated, or incomplete data provided by users or customers;'
              },
              {
                text: 'SkyHighGrowth Inc. is not responsible for the lawfulness of your collection and use of data (including training data and logs) that you upload or reference in CompliAI;'
              },
              {
                text: 'SkyHighGrowth Inc. is not liable for content accessible through third-party websites or services integrated with CompliAI (e.g., repositories, ticketing tools, communication platforms), nor for any damage that may arise from their use;'
              },
              {
                text: 'SkyHighGrowth Inc. is not liable for temporary unavailability, interruption, data loss, or other technical issues, provided reasonable technical and organisational measures have been implemented.'
              }
            ]
          }
        ]
      },
      {
        title: '8. Fees and Payment Terms',
        sections: [
          {
            type: 'paragraph',
            text: 'Use of CompliAI is subject to the applicable subscription plan, fees, and payment terms agreed in the order form, statement of work, or subscription page.'
          },

          {
            type: 'paragraph',
            text: 'Unless otherwise stated:'
          },

          {
            type: 'bullets',
            items: [
              {
                text: 'Fees are payable in advance for the relevant billing period and are non-refundable, except as explicitly provided in these Terms or in the applicable agreement.'
              },
              {
                text: 'All amounts are exclusive of applicable taxes (such as sales, use, VAT, or similar taxes), which will be added as required by law.'
              },
              {
                text: 'You are responsible for all bank charges, currency conversion fees, and other costs associated with making payments.'
              },
              {
                text: 'Failure to pay fees when due may result in suspension or termination of access to CompliAI.'
              }
            ]
          },

          {
            type: 'paragraph',
            text: 'Payments are processed through third-party payment processors or invoicing arrangements. Those processors may be subject to financial regulations in the United States and/or other jurisdictions (for example, the EU’s payments rules, such as PSD2, where relevant), but those regulatory regimes apply primarily to the payment providers, not to your contractual relationship with SkyHighGrowth Inc.'
          }
        ]
      },
      {
        title: '9. Governing Law and Jurisdiction',
        sections: [
          {
            type: 'paragraph',
            text: 'These Terms and any disputes arising out of or in connection with them shall be governed by and construed in accordance with the laws of the State of New York and, where applicable, the federal laws of the United States of America, without regard to conflict of law principles.'
          },
          {
            type: 'paragraph',
            text: 'The state and federal courts located in New York County, New York, USA, shall have exclusive jurisdiction over any dispute arising from or relating to these Terms or the use of CompliAI, unless mandatory law provides otherwise.'
          }
        ]
      },
      {
        title: '10. Privacy Rights',
        sections: [
          {
            type: 'paragraph',
            text: 'The privacy rights available to you depend on where you are located and which privacy laws apply to the processing of your personal data.'
          }
        ]
      },
      {
        title: '10.1 Rights under GDPR (EU/EEA/UK, where applicable)',
        sections: [
          {
            type: 'paragraph',
            text: 'Where SkyHighGrowth Inc. acts as data controller and the GDPR (or UK GDPR) applies, you may have the following rights:'
          },

          {
            type: 'bullets',
            items: [
              { text: 'right of access to your personal data;' },
              { text: 'right to rectification of inaccurate data;' },
              {
                text: 'right to erasure (“right to be forgotten”) in certain circumstances;'
              },
              { text: 'right to restriction of processing;' },
              { text: 'right to data portability (where applicable);' },
              {
                text: 'right to object to certain processing (including profiling);'
              },
              {
                text: 'right not to be subject to certain automated decisions with legal or similarly significant effects;'
              },
              {
                text: 'right to withdraw consent at any time, where processing is based on consent.'
              }
            ]
          },

          {
            type: 'paragraph',
            text: 'You may exercise these rights by contacting us at compliai@skyhighgrowth.com. You also have the right to lodge a complaint with a competent data protection authority in the EU/EEA or the UK.'
          },

          {
            type: 'paragraph',
            text: 'Where SkyHighGrowth Inc. processes personal data as processor/service provider on behalf of a Customer, we will forward or respond to your request in cooperation with that Customer, in accordance with our contractual obligations.'
          }
        ]
      },
      {
        title: '10.2 Rights under U.S. State Laws (e.g., California)',
        sections: [
          {
            type: 'paragraph',
            text: 'Certain U.S. states (for example, California under the CCPA/CPRA and others with similar laws) grant residents specific rights in relation to their personal information, such as:'
          },

          {
            type: 'bullets',
            items: [
              {
                text: 'right to know/access certain information about how personal information is collected and used;'
              },
              {
                text: 'right to request deletion of personal information, subject to exceptions;'
              },
              {
                text: 'right to correct inaccurate personal information;'
              },
              {
                text: 'right to opt out of certain “sales” or “sharing” of personal information;'
              },
              {
                text: 'right to be free from discrimination for exercising privacy rights.'
              }
            ]
          },

          {
            type: 'paragraph',
            text: 'Where such laws apply, we will honour requests in accordance with the relevant statute and our Privacy Policy. Requests can be submitted to compliai@skyhighgrowth.com or via any other channel described in the Privacy Policy.'
          }
        ]
      },
      {
        title: '10.3 Other Jurisdictions',
        sections: [
          {
            type: 'paragraph',
            text: 'If the laws of your jurisdiction grant you additional or different rights, we will respect those rights to the extent required by applicable law. Our Privacy Policy provides more detail on how to exercise your rights and how we verify and respond to requests.'
          }
        ]
      },
      {
        title: '11. Accessibility Statement',
        sections: [
          {
            type: 'paragraph',
            text: 'The CompliAI web interface is designed with the aim of being reasonably accessible to a broad range of users, including users with disabilities.'
          },

          {
            type: 'paragraph',
            text: 'In particular, we seek to align our user interface with:'
          },

          {
            type: 'bullets',
            items: [
              {
                text: 'the Web Content Accessibility Guidelines (WCAG) 2.1, Level AA, and'
              },
              {
                text: 'where applicable, accessibility obligations under U.S. law (such as the Americans with Disabilities Act (ADA) and Section 508 of the Rehabilitation Act),'
              },
              {
                text: 'and, for customers subject to EU accessibility requirements, relevant European accessibility standards, to the extent reasonably feasible for a B2B SaaS product.'
              }
            ]
          },

          {
            type: 'paragraph',
            text: 'SkyHighGrowth Inc. is committed to continually improving the accessibility of its digital services. Users can report accessibility issues or request reasonable accommodations by contacting compliai@skyhighgrowth.com.'
          },

          {
            type: 'paragraph',
            text: 'We will review all such requests in good faith and, where reasonable and technically feasible, will take steps to improve accessibility over time.'
          }
        ]
      },
      {
        title: '12. Changes and Final Provisions',
        sections: [
          {
            type: 'paragraph',
            text: 'These Terms enter into force on the date of their publication within the CompliAI application and/or on the SkyHighGrowth Inc. website.'
          },
          {
            type: 'paragraph',
            text: 'SkyHighGrowth Inc. reserves the right to amend or supplement these Terms at any time. Updated Terms will be published within the application and/or on the website. Where changes are material, SkyHighGrowth Inc. will make reasonable efforts to notify registered users (e.g., via e-mail or in-app notification).'
          },
          {
            type: 'paragraph',
            text: 'By continuing to use CompliAI after the effective date of updated Terms, you confirm that you have read, understood, and accepted the updated Terms.'
          }
        ]
      }
    ]
  },
  policy: {
    header: {
      title: 'Privacy Policy',
      description: ''
    },
    content: [
      {
        title: 'Privacy Policy for CompliAI',
        sections: [
          {
            type: 'paragraph',
            text: 'This Privacy Policy explains how SkyHighGrowth Inc. (“SkyHighGrowth”, “we”, “us”, “our”) collects, uses, shares, and protects personal data in connection with the CompliAI application and related services.'
          },

          {
            type: 'paragraph',
            text: 'CompliAI is a business-to-business (B2B) software-as-a-service (SaaS) platform that helps organisations understand and manage their obligations under the EU Artificial Intelligence Act (the “AI Act”) and related regulations. This Privacy Policy applies to:'
          },

          {
            type: 'bullets',
            items: [
              {
                text: 'individuals who use CompliAI as authorised users on behalf of a customer organisation;'
              },
              {
                text: 'representatives and contacts of our customers, partners, and suppliers; and'
              },
              {
                text: 'visitors to our websites and other online touchpoints where this Policy is posted.'
              }
            ]
          },

          {
            type: 'paragraph',
            text: 'This Privacy Policy should be read together with our Terms and Conditions for the CompliAI Application (“Terms”). Capitalised terms used but not defined here have the meanings given in the Terms.'
          }
        ]
      },
      {
        title: '1. Who We Are',
        sections: [
          {
            type: 'paragraph',
            text: 'Controller:'
          },
          {
            type: 'paragraph',
            text: 'For personal data that we collect and use for our own purposes (e.g., account administration, billing, marketing), SkyHighGrowth Inc. is the “data controller” (or equivalent concept under applicable law):'
          },
          {
            type: 'paragraphGroup',
            text: [
              'SkyHighGrowth Inc.',
              'EIN: 35-2651156',
              '276 5th Ave RM 704',
              'New York, NY 10001',
              'USA',
              'Email: compliai@skyhighgrowth.com'
            ]
          },
          {
            type: 'paragraph',
            text: 'Processor / Service Provider:'
          },
          {
            type: 'paragraph',
            text: 'For personal data that our customers upload or enter into CompliAI as part of their use of the platform (e.g., AI system documentation, risk assessments, policies, logs), we generally act as a data processor / service provider on behalf of the relevant customer (the “Customer”). In those cases, the Customer is the controller and is responsible for providing appropriate notices to individuals whose data they process through CompliAI.'
          }
        ]
      },
      {
        title: '2. Scope of This Privacy Policy',
        sections: [
          {
            type: 'paragraph',
            text: 'This Privacy Policy applies to personal data we process when you:'
          },

          {
            type: 'bullets',
            items: [
              { text: 'register for, access, or use CompliAI;' },
              {
                text: 'interact with us as a customer, prospect, supplier, or partner;'
              },
              {
                text: 'visit our website(s) or communicate with us via email, support channels, or social media.'
              }
            ]
          },

          {
            type: 'paragraph',
            text: 'This Privacy Policy does not apply to:'
          },

          {
            type: 'bullets',
            items: [
              {
                text: 'personal data handled solely by our Customers inside their own systems;'
              },
              {
                text: 'processing of personal data by third-party tools integrated with CompliAI (e.g., ticketing tools, code repositories, communication platforms) where those third parties act as independent controllers.'
              }
            ]
          },

          {
            type: 'paragraph',
            text: 'In those cases, the privacy policies of the relevant third parties and/or Customer apply.'
          }
        ]
      },
      {
        title: '3. Categories of Personal Data We Collect',
        sections: [
          {
            type: 'paragraph',
            text: 'The personal data we collect depends on how you interact with us and how CompliAI is configured by your organisation. We may collect the following categories:'
          }
        ]
      },
      {
        title: '3.1 User and Account Data',
        sections: [
          {
            type: 'bullets',
            items: [
              { text: 'Identification details: first name, last name.' },
              {
                text: 'Contact details: business email address, optionally phone number.'
              },
              {
                text: 'Professional details: job title, role, company name, department.'
              },
              {
                text: 'Account and credentials: username, hashed passwords (where applicable), authentication tokens, role/permission assignments.'
              }
            ]
          }
        ]
      },
      {
        title: '3.2 Usage and System Data',
        sections: [
          {
            type: 'bullets',
            items: [
              {
                text: 'Log data: application logs, actions performed (e.g., login, view, edit, export), timestamps.'
              },
              {
                text: 'Device and technical data: IP address, browser type and version, operating system, language settings.'
              },
              {
                text: 'Session data: pages viewed, features used, time spent, error messages.'
              },
              {
                text: 'Configuration: tenant settings, integration settings, notification preferences.'
              }
            ]
          }
        ]
      },
      {
        title: '3.3 Customer Content',
        sections: [
          {
            type: 'bullets',
            items: [
              {
                text: 'Information input or uploaded by users into CompliAI on behalf of a Customer, such as:',
                subItems: [
                  'AI system descriptions, risk classification and assessments, use-case documentation;',
                  'policies, procedures, technical documentation, and audit trails;',
                  'logs, test results, and other compliance documentation.'
                ]
              }
            ]
          },
          {
            type: 'paragraph',
            text: 'This Customer Content may include personal data if your organisation chooses to include such information (e.g., names of responsible persons, reviewers, or stakeholders). For this type of data, we typically act as processor/service provider; the Customer decides what to upload or store.'
          }
        ]
      },
      {
        title: '3.4 Communication and Support Data',
        sections: [
          {
            type: 'bullets',
            items: [
              {
                text: 'Content of communications with us (e.g., support tickets, email exchanges, feedback, survey responses).'
              },
              {
                text: 'Metadata related to support interactions (timestamps, assigned agent, resolution status).'
              }
            ]
          }
        ]
      },
      {
        title: '3.5 Marketing and Sales Data',
        sections: [
          {
            type: 'bullets',
            items: [
              {
                text: 'Contact information and professional details of prospects and business contacts.'
              },
              {
                text: 'Information about your interactions with our marketing (e.g., opening newsletters, attending webinars, responding to campaigns), subject to applicable consent requirements.'
              }
            ]
          }
        ]
      },
      {
        title: '4. How We Collect Personal Data',
        sections: [
          {
            type: 'paragraph',
            text: 'We collect personal data from the following sources:'
          },

          {
            type: 'bullets',
            items: [
              {
                text: 'Directly from you: when you register, log in, update your profile, submit forms, contact support, or otherwise communicate with us.'
              },
              {
                text: 'From your organisation: when your employer or contracting entity sets you up as an authorised user or provides your details as a contact person.'
              },
              {
                text: 'Automatically from your use of CompliAI: through cookies, logs, and similar technologies that capture usage and device information.'
              },
              {
                text: 'From third parties: such as integration partners, payment providers, or marketing platforms, when permitted by law and in line with this Policy.'
              }
            ]
          }
        ]
      },
      {
        title: '5. Purposes and Legal Bases for Processing',
        sections: [
          {
            type: 'paragraph',
            text: 'We process personal data only when we have a valid legal basis under applicable law. Depending on the context, we may process your personal data for the following purposes:'
          }
        ]
      },
      {
        title: '5.1 Provision of CompliAI and Account Administration',
        sections: [
          {
            type: 'bullets',
            items: [
              { text: 'Creating and managing user accounts and tenants.' },
              { text: 'Providing access to CompliAI and its features.' },
              {
                text: 'Handling authentication, authorisation, and user management.'
              },
              { text: 'Providing support and resolving technical issues.' }
            ]
          },
          {
            type: 'paragraph',
            text: 'Legal bases (where applicable):'
          },
          {
            type: 'bullets',
            items: [
              { text: 'Performance of a contract;' },
              {
                text: 'Legitimate interests (e.g., provision and improvement of services to our customers).'
              }
            ]
          }
        ]
      },
      {
        title: '5.2 Security, Fraud Prevention, and Abuse Detection',
        sections: [
          {
            type: 'bullets',
            items: [
              { text: 'Monitoring access and activity for security purposes.' },
              {
                text: 'Detecting and investigating suspicious or fraudulent behaviour.'
              },
              {
                text: 'Protecting CompliAI, our infrastructure, and our customers from misuse.'
              }
            ]
          },
          {
            type: 'paragraph',
            text: 'Legal bases:'
          },
          {
            type: 'bullets',
            items: [
              {
                text: 'Legitimate interests (ensuring security and integrity of our services);'
              },
              { text: 'Compliance with legal obligations, where relevant.' }
            ]
          }
        ]
      },
      {
        title: '5.3 Service Improvement and Analytics',
        sections: [
          {
            type: 'bullets',
            items: [
              {
                text: 'Analysing usage patterns and performance to improve functionality, reliability, and user experience.'
              },
              { text: 'Developing new features, workflows, and integrations.' },
              { text: 'Generating aggregated or anonymised statistics.' }
            ]
          },
          {
            type: 'paragraph',
            text: 'Legal bases:'
          },
          {
            type: 'bullets',
            items: [
              {
                text: 'Legitimate interests (improving and developing our services);'
              },
              {
                text: 'Consent, where required (e.g., for certain types of analytics or cookies).'
              }
            ]
          }
        ]
      },
      {
        title: '5.4 Billing, Finance, and Contract Management',
        sections: [
          {
            type: 'bullets',
            items: [
              {
                text: 'Managing subscriptions, invoicing, and payment processing.'
              },
              { text: 'Keeping business, tax, and accounting records.' },
              {
                text: 'Managing contracts with customers, suppliers, and partners.'
              }
            ]
          },
          {
            type: 'paragraph',
            text: 'Legal bases:'
          },
          {
            type: 'bullets',
            items: [
              { text: 'Performance of a contract;' },
              { text: 'Compliance with legal obligations;' },
              { text: 'Legitimate interests (business administration).' }
            ]
          }
        ]
      },
      {
        title: '5.5 Communication and Marketing',
        sections: [
          {
            type: 'bullets',
            items: [
              {
                text: 'Responding to enquiries and providing customer support.'
              },
              {
                text: 'Sending service-related notifications (e.g., security alerts, feature updates).'
              },
              {
                text: 'Sending marketing communications (e.g., newsletters, event invitations) where permitted.'
              }
            ]
          },
          {
            type: 'paragraph',
            text: 'Legal bases:'
          },
          {
            type: 'bullets',
            items: [
              { text: 'Performance of a contract (service messages);' },
              {
                text: 'Legitimate interests (customer relationship management and B2B marketing);'
              },
              {
                text: 'Consent, where required by law (e.g., for certain email marketing).'
              }
            ]
          },
          {
            type: 'paragraph',
            text: 'You can opt out of marketing communications at any time by using the unsubscribe link in our emails or contacting us at compliai@skyhighgrowth.com.'
          }
        ]
      },
      {
        title: '6. Cookies and Similar Technologies',
        sections: [
          {
            type: 'paragraph',
            text: 'We use cookies and similar technologies (such as local storage, pixels, and scripts) to:'
          },
          {
            type: 'bullets',
            items: [
              { text: 'keep you signed in and manage sessions;' },
              { text: 'remember your preferences and settings;' },
              {
                text: 'perform basic analytics on how CompliAI and our websites are used;'
              },
              { text: 'improve performance and security.' }
            ]
          },
          {
            type: 'paragraph',
            text: 'Where required by law, we will ask for your consent before placing non-essential cookies. You can manage your cookie preferences via your browser settings and, where available, in-product cookie banners or settings.'
          }
        ]
      },
      {
        title: '7. How We Share Personal Data',
        sections: [
          {
            type: 'paragraph',
            text: 'We do not sell personal data in the sense of exchanging it for money. We may share personal data in the following situations:'
          }
        ]
      },
      {
        title: '7.1 Service Providers and Sub-Processors',
        sections: [
          {
            type: 'paragraph',
            text: 'We engage third-party service providers (sub-processors) to support CompliAI and our business operations, for example:'
          },
          {
            type: 'bullets',
            items: [
              { text: 'cloud hosting and infrastructure;' },
              { text: 'logging, monitoring, and analytics tools;' },
              { text: 'email and notification services;' },
              { text: 'payment processors;' },
              { text: 'customer support and CRM tools.' }
            ]
          },
          {
            type: 'paragraph',
            text: 'These providers process personal data only on our documented instructions, for the purposes identified in this Policy, and are bound by confidentiality and data protection obligations.'
          }
        ]
      },
      {
        title: '7.2 Customers and Authorised Users',
        sections: [
          {
            type: 'paragraph',
            text: 'Depending on how your organisation uses CompliAI:'
          },
          {
            type: 'bullets',
            items: [
              {
                text: 'certain information may be visible to other users within the same tenant (e.g., your name, email, actions in audit logs);'
              },
              {
                text: 'administrators designated by your organisation may access your account information, activity records, and Customer Content.'
              }
            ]
          }
        ]
      },
      {
        title: '7.3 Legal Obligations and Protection of Rights',
        sections: [
          {
            type: 'paragraph',
            text: 'We may disclose personal data if required to do so by law or in good faith belief that such action is necessary to:'
          },
          {
            type: 'bullets',
            items: [
              {
                text: 'comply with a legal obligation, court order, or government request;'
              },
              {
                text: 'protect and defend our rights, property, or safety, or those of our customers, users, or the public;'
              },
              {
                text: 'detect, prevent, or address fraud, security, or technical issues.'
              }
            ]
          }
        ]
      },
      {
        title: '7.4 Business Transfers',
        sections: [
          {
            type: 'paragraph',
            text: 'In connection with a merger, acquisition, reorganisation, sale of assets, or similar transaction, personal data may be transferred as part of the business assets, subject to appropriate safeguards and continued protection consistent with this Policy.'
          }
        ]
      },
      {
        title: '8. International Data Transfers',
        sections: [
          {
            type: 'paragraph',
            text: 'SkyHighGrowth Inc. is based in the United States. Depending on your location and how services are implemented, your personal data may be transferred to, stored in, or accessed from countries outside your own, including countries that may not provide the same level of data protection as your home jurisdiction.'
          },
          {
            type: 'paragraph',
            text: 'Where required by law (for example, under the GDPR), we implement appropriate safeguards for such transfers, which may include:'
          },
          {
            type: 'bullets',
            items: [
              { text: 'relying on adequacy decisions;' },
              {
                text: 'entering into Standard Contractual Clauses or equivalent instruments;'
              },
              {
                text: 'applying additional technical and organisational measures to protect data.'
              }
            ]
          },
          {
            type: 'paragraph',
            text: 'Further details on applicable transfer mechanisms can be provided upon request.'
          }
        ]
      },
      {
        title: '9. Data Retention',
        sections: [
          {
            type: 'paragraph',
            text: 'We retain personal data only for as long as necessary for the purposes described in this Policy, including to:'
          },
          {
            type: 'bullets',
            items: [
              { text: 'provide CompliAI and related services;' },
              {
                text: 'comply with legal, accounting, and regulatory obligations;'
              },
              { text: 'resolve disputes and enforce agreements.' }
            ]
          },
          {
            type: 'paragraph',
            text: 'In general:'
          },
          {
            type: 'bullets',
            items: [
              {
                text: 'User and account data is retained for the duration of the customer relationship and a reasonable period thereafter (e.g., to manage renewals, disputes, or legal obligations).'
              },
              {
                text: 'Customer Content is retained in accordance with the customer’s configuration and the applicable contract; it is typically deleted within a defined period after account closure or at the customer’s instruction.'
              },
              {
                text: 'Logs and technical data are retained for shorter periods, unless longer retention is needed for security, troubleshooting, or legal reasons.'
              }
            ]
          },
          {
            type: 'paragraph',
            text: 'When data is no longer needed, we will delete it or irreversibly anonymise it.'
          }
        ]
      },
      {
        title: '10. Security',
        sections: [
          {
            type: 'paragraph',
            text: 'We implement reasonable and appropriate technical and organisational measures to protect personal data against unauthorised access, loss, misuse, alteration, or destruction, taking into account the state of the art, implementation costs, and the nature, scope, context, and purposes of processing.'
          },
          {
            type: 'paragraph',
            text: 'Such measures may include:'
          },
          {
            type: 'bullets',
            items: [
              { text: 'access controls and role-based permissions;' },
              {
                text: 'encryption in transit (e.g., TLS) and, where appropriate, at rest;'
              },
              { text: 'network and application security measures;' },
              { text: 'logging and monitoring of access and system events;' },
              { text: 'backup and disaster recovery processes;' },
              {
                text: 'security awareness and confidentiality obligations for personnel and contractors.'
              }
            ]
          },
          {
            type: 'paragraph',
            text: 'No system can be guaranteed to be 100% secure. Customers also play a crucial role in security by:'
          },
          {
            type: 'bullets',
            items: [
              { text: 'protecting their credentials and access tokens;' },
              {
                text: 'implementing internal policies for user access and data handling;'
              },
              {
                text: 'promptly notifying us of suspected security incidents related to CompliAI.'
              }
            ]
          }
        ]
      },
      {
        title: '11. Your Privacy Rights',
        sections: [
          {
            type: 'paragraph',
            text: 'Your privacy rights depend on your location and the laws that apply to our processing of your personal data.'
          }
        ]
      },
      {
        title: '11.1 Rights under GDPR (EU/EEA/UK, where applicable)',
        sections: [
          {
            type: 'paragraph',
            text: 'Where we act as data controller and the GDPR (or UK GDPR) applies, you may have the right to:'
          },
          {
            type: 'bullets',
            items: [
              { text: 'Access your personal data and obtain a copy.' },
              { text: 'Rectify inaccurate or incomplete data.' },
              { text: 'Erase your personal data in certain circumstances.' },
              {
                text: 'Restrict the processing of your data in certain circumstances.'
              },
              {
                text: 'Object to processing based on our legitimate interests (including profiling).'
              },
              {
                text: 'Data portability, where processing is based on consent or contract and carried out by automated means.'
              },
              {
                text: 'Withdraw consent at any time (without affecting the lawfulness of processing based on consent before its withdrawal).'
              },
              {
                text: 'Lodge a complaint with a competent data protection authority.'
              }
            ]
          },
          {
            type: 'paragraph',
            text: 'Where we process personal data as processor/service provider on behalf of a Customer, we will assist the Customer in responding to such requests, in accordance with our contractual obligations.'
          }
        ]
      },
      {
        title: '11.2 Rights under U.S. State Privacy Laws (e.g., California)',
        sections: [
          {
            type: 'paragraph',
            text: 'Certain U.S. states (such as California under the CCPA/CPRA) grant residents specific rights, which may include:'
          },
          {
            type: 'bullets',
            items: [
              {
                text: 'right to know/access certain information about how personal information is collected, used, and disclosed;'
              },
              {
                text: 'right to request deletion of personal information, subject to exceptions;'
              },
              { text: 'right to correct inaccurate personal information;' },
              {
                text: 'right to opt out of certain “sales” or “sharing” of personal information, where applicable;'
              },
              {
                text: 'right not to be discriminated against for exercising privacy rights.'
              }
            ]
          },
          {
            type: 'paragraph',
            text: 'Where such laws apply, you can exercise your rights by contacting us at compliai@skyhighgrowth.com and we will respond in accordance with applicable law.'
          }
        ]
      },
      {
        title: '11.3 Exercising Your Rights',
        sections: [
          {
            type: 'paragraph',
            text: 'To exercise any of your rights under applicable data protection laws, please contact us at: compliai@skyhighgrowth.com.'
          },
          {
            type: 'paragraph',
            text: 'We may need to verify your identity before responding to your request. Where we are acting as a processor on behalf of a Customer, we may redirect your request to the relevant Customer.'
          }
        ]
      },
      {
        title: '12. Children’s Privacy',
        sections: [
          {
            type: 'paragraph',
            text: 'CompliAI is a business-focused platform and is not intended for use by children under the age of 16 (or such other age as defined by applicable law in your jurisdiction). We do not knowingly collect personal data from children via CompliAI.'
          },
          {
            type: 'paragraph',
            text: 'If you believe that we may have collected personal data from a child inappropriately, please contact us at compliai@skyhighgrowth.com so we can investigate and take appropriate action.'
          }
        ]
      },
      {
        title: '13. Changes to This Privacy Policy',
        sections: [
          {
            type: 'paragraph',
            text: 'We may update this Privacy Policy from time to time to reflect changes in our practices, technologies, legal requirements, or other factors.'
          },
          {
            type: 'paragraph',
            text: 'When we make material changes, we will:'
          },
          {
            type: 'bullets',
            items: [
              {
                text: 'post the updated Policy within CompliAI and/or on our website, and'
              },
              {
                text: 'take reasonable steps to notify registered users (e.g., via email or in-app notification).'
              }
            ]
          },
          {
            type: 'paragraph',
            text: 'The “Last updated” date at the top of this Policy indicates when it was last revised. By continuing to use CompliAI after the updated Policy becomes effective, you acknowledge that you have read and understood the changes.'
          }
        ]
      },
      {
        title: '14. Contact Us',
        sections: [
          {
            type: 'paragraph',
            text: 'If you have any questions, concerns, or requests related to this Privacy Policy or our handling of personal data, you can contact us at:'
          },
          {
            type: 'paragraphGroup',
            text: [
              'SkyHighGrowth Inc.',
              '276 5th Ave RM 704',
              'New York, NY 10001',
              'USA',
              'Email: compliai@skyhighgrowth.com'
            ]
          }
        ]
      }
    ]
  }
};
